/** Zonas IANA — el motor del SO aplica invierno/verano (DST) automáticamente. */

export const PRIMARY_TIMEZONE_IDS = [
  'America/Mexico_City',
  'America/Santiago',
  'America/New_York',
  'America/Los_Angeles',
  'America/Chicago',
  'America/Denver',
  'America/Bogota',
  'America/Buenos_Aires',
  'America/Lima',
  'UTC',
];

export const EXTENDED_TIMEZONE_IDS = [
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Australia/Sydney',
  'Pacific/Auckland',
];

export const REPORT_TIMEZONE_IDS = [
  ...PRIMARY_TIMEZONE_IDS.filter((id) => id !== 'UTC'),
  ...EXTENDED_TIMEZONE_IDS,
  'UTC',
];

const FRIENDLY_NAMES = {
  'America/Mexico_City': 'México',
  'America/Santiago': 'Chile',
  'America/New_York': 'Nueva York',
  'America/Los_Angeles': 'Los Ángeles',
  'America/Chicago': 'Chicago',
  'America/Denver': 'Denver',
  'America/Bogota': 'Bogotá',
  'America/Buenos_Aires': 'Buenos Aires',
  'America/Lima': 'Lima',
  'America/Sao_Paulo': 'São Paulo',
  'Europe/London': 'Londres',
  'Europe/Paris': 'París',
  'Europe/Madrid': 'Madrid',
  'Europe/Berlin': 'Berlin',
  'Asia/Tokyo': 'Tokio',
  'Asia/Shanghai': 'Shanghái',
  'Asia/Dubai': 'Dubai',
  'Asia/Kolkata': 'India',
  'Australia/Sydney': 'Sydney',
  'Pacific/Auckland': 'Auckland',
  UTC: 'UTC',
};

export function defaultBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City';
  } catch {
    return 'America/Mexico_City';
  }
}

/** Offset actual según fecha (respeta DST), ej. "GMT-04:00". */
export function formatTimezoneOffset(timeZoneId, locale = 'es') {
  if (!timeZoneId) return '';
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone: timeZoneId,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
}

export function formatTimezoneLabel(timeZoneId, locale = 'es') {
  if (!timeZoneId) return '';
  const name =
    FRIENDLY_NAMES[timeZoneId] ||
    String(timeZoneId)
      .split('/')
      .pop()
      .replace(/_/g, ' ');
  const offset = formatTimezoneOffset(timeZoneId, locale);
  return offset ? `${name} (${offset})` : name;
}

export function isKnownTimezone(timeZoneId, list = PRIMARY_TIMEZONE_IDS) {
  return list.includes(timeZoneId);
}

export function resolveTimezoneSelection(value, fallback = 'America/Mexico_City', list = PRIMARY_TIMEZONE_IDS) {
  if (value && list.includes(value)) return value;
  return fallback;
}

const DASHBOARD_TIMEZONE_STORAGE_KEY = 'bi.dashboardTimezone';

function timezoneStorageKey(dashboardId) {
  if (dashboardId == null || dashboardId === '') return DASHBOARD_TIMEZONE_STORAGE_KEY;
  return `${DASHBOARD_TIMEZONE_STORAGE_KEY}.${String(dashboardId)}`;
}

function readStorageValue(key) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw || !String(raw).trim()) return null;
    return String(raw).trim();
  } catch {
    return null;
  }
}

export function readStoredTimezone(dashboardId) {
  return readStorageValue(timezoneStorageKey(dashboardId));
}

export function loadPreferredTimezone(fallback = 'America/Mexico_City') {
  return resolveTimezoneSelection(readStoredTimezone(), fallback);
}

export function savePreferredTimezone(timeZoneId, dashboardId) {
  if (typeof window === 'undefined') return;
  const value = resolveTimezoneSelection(timeZoneId);
  try {
    window.localStorage.setItem(DASHBOARD_TIMEZONE_STORAGE_KEY, value);
    if (dashboardId != null && dashboardId !== '') {
      window.localStorage.setItem(timezoneStorageKey(dashboardId), value);
    }
  } catch {
    /* quota / private mode */
  }
}

export function resolveViewTimezone(dashboard, fallback = 'America/Mexico_City') {
  const storedForDashboard =
    dashboard?.id != null && dashboard.id !== '' ? readStoredTimezone(dashboard.id) : null;
  if (storedForDashboard) return resolveTimezoneSelection(storedForDashboard, fallback);
  if (dashboard?.timezone) return resolveTimezoneSelection(dashboard.timezone, fallback);
  return loadPreferredTimezone(fallback);
}
